// src/utils/eventUtils.js

import { ACTIVITY_DEFAULT_SCHEDULE_DAY } from '../constants/eventActivityDates';

/**
 * Normalize an event's date field to a JS Date (local time, no time-of-day precision).
 * The event date is always the Saturday of the event week.
 * Supports Firestore Timestamp (with toDate), YYYY-MM-DD (parsed as local), other date strings, or Date.
 * Returns null if a valid date cannot be determined.
 */
export function getEventDate(event) {
  if (!event || !event.date) return null;
  let d = null;

  const raw = event.date;
  if (raw && typeof raw.toDate === 'function') {
    d = raw.toDate();
  } else if (raw instanceof Date) {
    d = raw;
  } else {
    const s = String(raw).trim();
    if (!s) return null;
    // HTML5 date input / YYYY-MM-DD: parse as local date to avoid timezone shift
    const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, day] = isoMatch;
      const month = parseInt(m, 10) - 1;
      if (month >= 0 && month <= 11) {
        d = new Date(parseInt(y, 10), month, parseInt(day, 10));
        if (!Number.isNaN(d.getTime())) {
          return d;
        }
      }
    }
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }

  if (!d) return null;

  // Strip time-of-day; treat the event date as local midnight.
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Parse a single calendar value (ISO date, Timestamp, Date) to local calendar midnight.
 * Used for scheduleDayDates and activityDates overrides.
 */
export function parseCalendarDayValue(raw) {
  if (raw == null || raw === '') return null;
  if (raw && typeof raw.toDate === 'function') {
    const d = raw.toDate();
    if (!d || Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null;
    return new Date(raw.getFullYear(), raw.getMonth(), raw.getDate());
  }
  const s = String(raw).trim();
  if (!s) return null;
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, day] = isoMatch;
    const month = parseInt(m, 10) - 1;
    if (month >= 0 && month <= 11) {
      const d = new Date(parseInt(y, 10), month, parseInt(day, 10));
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** Map schedule day id to offset from Saturday (event day). Monday = after party (two days after Saturday). */
const SCHEDULE_DAY_OFFSET = {
  wednesday: -3,
  thursday: -2,
  friday: -1,
  saturday: 0,
  sunday: 1,
  monday: 2,
};

function getScheduleDayDateFromSaturdayAnchor(event, dayId) {
  const saturday = getEventDate(event);
  if (!saturday || !dayId) return null;
  const offset = SCHEDULE_DAY_OFFSET[String(dayId).toLowerCase()];
  if (offset == null) return null;
  const d = new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate());
  d.setDate(d.getDate() + offset);
  return d;
}

/**
 * Resolved calendar date for a schedule column (wednesday…monday).
 * Uses events.scheduleDayDates[dayId] when set; otherwise derives from Saturday anchor.
 */
export function getResolvedScheduleDayDate(event, dayId) {
  const key = String(dayId || '').toLowerCase();
  if (!key) return null;
  const raw = event?.scheduleDayDates?.[key];
  if (raw != null && String(raw).trim() !== '') {
    const d = parseCalendarDayValue(raw);
    if (d) return d;
  }
  return getScheduleDayDateFromSaturdayAnchor(event, key);
}

/**
 * Returns true once the event week has begun (first public day = Wednesday by default).
 * Uses resolved Wednesday date so scheduleDayDates.wednesday is respected.
 */
export function hasEventStarted(event) {
  const start = getEventOperationalStartDate(event);
  if (!start) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return today.getTime() >= start.getTime();
}

/** First day of the on-site week (resolved Wednesday). */
export function getEventOperationalStartDate(event) {
  return getResolvedScheduleDayDate(event, 'wednesday');
}

/** Day names for event week (Wednesday through Monday after party), with Total first. */
const EVENT_DAY_ORDER = ['Total', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Monday'];

/**
 * Offsets from Saturday (event day = 0): Wednesday -3 … Sunday +1, Monday +2 (after party).
 * Index 0 is Total (no date).
 */
const DAY_OFFSETS = [null, -3, -2, -1, 0, 1, 2];

/**
 * Returns day headers with dates derived from the event date (Saturday).
 * e.g. [..., 'Sunday, March 22', 'Monday, March 23'] (Monday = after party).
 * If event or event.date is missing/invalid, returns plain day names without dates.
 */
export function getDayHeadersWithDates(event) {
  const saturday = getEventDate(event);
  if (!saturday) {
    return [...EVENT_DAY_ORDER];
  }
  return EVENT_DAY_ORDER.map((name, i) => {
    const offset = DAY_OFFSETS[i];
    if (offset == null) return name;
    const dayIds = ['wednesday', 'thursday', 'friday', 'saturday', 'sunday', 'monday'];
    const dayId = dayIds[i - 1];
    const d = getResolvedScheduleDayDate(event, dayId);
    if (!d) return name;
    const dateStr = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${name}, ${dateStr}`;
  });
}

/**
 * Returns the date string (e.g. "March 18") for a schedule day id, given the event (Saturday = event day).
 * Returns null if event date is missing or dayId is unknown.
 */
export function getDayDateString(event, dayId) {
  const d = getResolvedScheduleDayDate(event, dayId);
  if (!d) return null;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/**
 * Returns the Date (midnight local) for a schedule day id, given the event (Saturday = event day).
 * Returns null if event date is missing or dayId is unknown.
 */
export function getScheduleDayDate(event, dayId) {
  return getResolvedScheduleDayDate(event, dayId);
}

/**
 * Returns a short date range string with year for the event week through after party (Wed–Mon).
 * e.g. "March 18–23, 2025". Uses resolved Wednesday and Monday (schedule overrides when set).
 * Returns null if event or event.date is missing/invalid.
 */
export function getEventDateRangeWithYear(event) {
  const wed = getResolvedScheduleDayDate(event, 'wednesday');
  const mon = getResolvedScheduleDayDate(event, 'monday');
  if (!wed || !mon) return null;
  const year = wed.getFullYear();
  const monthWed = wed.toLocaleDateString('en-US', { month: 'long' });
  const monthMon = mon.toLocaleDateString('en-US', { month: 'long' });
  const dayWed = wed.getDate();
  const dayMon = mon.getDate();
  if (monthWed === monthMon) {
    return `${monthWed} ${dayWed}–${dayMon}, ${year}`;
  }
  return `${wed.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – ${mon.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}, ${year}`;
}

/** Calendar date for the after party (Monday after main Saturday by default). */
export function getAfterPartyDate(event) {
  return getResolvedScheduleDayDate(event, 'monday');
}

/** Long label for after-party emails, e.g. "Monday, March 23, 2026". */
export function formatAfterPartyDateLabel(event) {
  const d = getAfterPartyDate(event);
  if (!d) return '';
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Calendar Date for an activity (Fury Lab, recurrency, etc.).
 * Uses events.activityDates[key] when set; otherwise default weekday from ACTIVITY_DEFAULT_SCHEDULE_DAY.
 */
export function getActivityCalendarDate(event, activityKey) {
  const key = String(activityKey || '').trim();
  if (!key) return null;
  const rawOverride = event?.activityDates?.[key];
  if (rawOverride != null && String(rawOverride).trim() !== '') {
    const d = parseCalendarDayValue(rawOverride);
    if (d) return d;
  }
  const dayId = ACTIVITY_DEFAULT_SCHEDULE_DAY[key];
  if (!dayId) return null;
  return getResolvedScheduleDayDate(event, dayId);
}

/** Long date string for merges (local calendar; matches YYYY-MM-DD / local-midnight Dates from admin inputs). */
export function formatActivityDateForMerge(d) {
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function getFuryLabDateLabel(event) {
  return formatActivityDateForMerge(getActivityCalendarDate(event, 'furyLab'));
}

export function getRecurrencyWeekdayName(event) {
  const d = getActivityCalendarDate(event, 'recurrency');
  if (!d) return 'Wednesday';
  return d.toLocaleDateString('en-US', { weekday: 'long' });
}

function escapeHtmlAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');
}

function buildActivityDatesListHtml(event) {
  const rows = [
    ['Recurrency', getActivityCalendarDate(event, 'recurrency')],
    ['Fury Lab', getActivityCalendarDate(event, 'furyLab')],
    ['Water training (Thursday)', getActivityCalendarDate(event, 'waterTrainingThursday')],
    ['Water training (Friday)', getActivityCalendarDate(event, 'waterTrainingFriday')],
    ['Canopy course (Thursday)', getActivityCalendarDate(event, 'canopyThursday')],
    ['Canopy course (Friday)', getActivityCalendarDate(event, 'canopyFriday')],
    ['High altitude loads', getActivityCalendarDate(event, 'highAltitude')],
    ['After party', getAfterPartyDate(event)],
  ];
  const items = rows
    .map(([label, d]) => {
      const formatted = formatActivityDateForMerge(d);
      if (!formatted) return '';
      return `<li><strong>${escapeHtmlAttr(label)}:</strong> ${escapeHtmlAttr(formatted)}</li>`;
    })
    .filter(Boolean);
  if (!items.length) return '';
  return `<ul>${items.join('')}</ul>`;
}

/**
 * Flat object merged into mail-merge data for {{eventWeekRange}}, {{furyLabDate}}, etc.
 */
export function getEventDateMergePack(event) {
  const empty = {
    eventWeekRange: '',
    eventDateRange: '',
    recurrencyDate: '',
    recurrencyWeekday: '',
    furyLabDate: '',
    waterTrainingThursdayDate: '',
    waterTrainingFridayDate: '',
    canopyThursdayDate: '',
    canopyFridayDate: '',
    highAltitudeDate: '',
    afterPartyDate: '',
    activityDatesHtml: '',
  };
  if (!event) return empty;

  const weekRange = getEventDateRangeWithYear(event) || '';

  return {
    eventWeekRange: weekRange,
    eventDateRange: weekRange,
    recurrencyDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'recurrency')),
    recurrencyWeekday: getRecurrencyWeekdayName(event),
    furyLabDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'furyLab')),
    waterTrainingThursdayDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'waterTrainingThursday')),
    waterTrainingFridayDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'waterTrainingFriday')),
    canopyThursdayDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'canopyThursday')),
    canopyFridayDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'canopyFriday')),
    highAltitudeDate: formatActivityDateForMerge(getActivityCalendarDate(event, 'highAltitude')),
    afterPartyDate: formatAfterPartyDateLabel(event),
    activityDatesHtml: buildActivityDatesListHtml(event),
  };
}
